import React from "react";
import { Clock, Coffee, Sparkles } from "lucide-react";
import { useLang } from "@/i18n/LanguageProvider";
import { formatIDR } from "@/i18n/FxProvider";
import { IMG, FOOD_GALLERY } from "@/lib/images";
import { Reveal } from "@/components/Reveal";
import { waUrl, ORDER_MESSAGE } from "@/lib/contact";
import {
  HOURS, BREAKFAST, INDONESIAN, WESTERN, SNACKS, DESSERTS, COFFEE, NON_COFFEE,
  JUICES_SIGNATURE, JUICES_FRUIT, SMOOTHIE_BOWLS,
} from "@/lib/menu";

function PriceRow({ name, price, desc }) {
  return (
    <div className="flex items-baseline justify-between gap-4 py-3 border-b border-border last:border-0">
      <div>
        <div className="text-ink">{name}</div>
        {desc && <div className="text-sm text-ink/60 mt-1 max-w-prose">{desc}</div>}
      </div>
      {price != null && <div className="font-display text-ink whitespace-nowrap">{formatIDR(price)}</div>}
    </div>
  );
}

function HotIceRow({ name, hot, ice, note, signature }) {
  return (
    <div className="flex items-baseline justify-between gap-4 py-3 border-b border-border last:border-0">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-ink">{name}</span>
        {signature && (
          <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.2em] text-cocoa bg-cocoa/8 px-2 py-0.5 rounded-full">
            <Sparkles className="w-3 h-3" /> Garden Signature
          </span>
        )}
        {note && <span className="text-xs text-ink/55">({note})</span>}
      </div>
      <div className="font-display text-ink whitespace-nowrap text-sm sm:text-base">
        {formatIDR(hot)}<span className="text-ink/40 font-body"> hot</span>
        {ice != null && (
          <>
            <span className="mx-2 text-ink/30">/</span>
            {formatIDR(ice)}<span className="text-ink/40 font-body"> ice</span>
          </>
        )}
      </div>
    </div>
  );
}

function Section({ title, subtitle, children, id }) {
  return (
    <Reveal>
      <section id={id} className="mt-14" data-testid={`menu-${id}`}>
        <div className="flex items-end justify-between gap-4 mb-4">
          <div>
            <h3 className="font-display text-2xl sm:text-3xl text-ink">{title}</h3>
            {subtitle && <p className="text-sm text-ink/60 mt-1 italic">{subtitle}</p>}
          </div>
        </div>
        <div className="divide-y divide-border bg-white/60 backdrop-blur border border-white rounded-3xl px-6 py-3">
          {children}
        </div>
      </section>
    </Reveal>
  );
}

export default function Restaurant() {
  const { t } = useLang();
  const c = t.restaurant.categories;

  return (
    <div data-testid="page-restaurant">
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="container-px mx-auto grid lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-6 pt-4">
            <div className="eyebrow mb-4">{t.restaurant.eyebrow}</div>
            <h1 className="font-display text-5xl sm:text-6xl text-ink leading-[1] text-balance">
              {t.restaurant.title}
            </h1>
            <p className="mt-5 text-lg text-ink/70 max-w-xl">{t.restaurant.subtitle}</p>
            <div className="mt-7 flex flex-wrap gap-3">
              <div className="inline-flex items-center gap-2 rounded-full bg-white/70 px-4 py-2 text-sm border border-border">
                <Clock className="w-4 h-4 text-moss" /> Breakfast {HOURS.breakfast}
              </div>
              <div className="inline-flex items-center gap-2 rounded-full bg-white/70 px-4 py-2 text-sm border border-border">
                <Coffee className="w-4 h-4 text-moss" /> Main Menu {HOURS.main}
              </div>
            </div>
            <a
              href={waUrl(ORDER_MESSAGE())}
              target="_blank"
              rel="noreferrer"
              data-testid="restaurant-order-btn"
              className="mt-8 inline-flex items-center gap-2 rounded-full bg-wa text-white px-6 py-3 text-sm shadow-md hover:-translate-y-0.5 transition-all"
            >
              {t.common.orderNow}
            </a>
          </div>
          <div className="lg:col-span-6 grid grid-cols-2 gap-4">
            <div className="overflow-hidden rounded-3xl aspect-[3/4]">
              <img src={FOOD_GALLERY[0]} alt="Signature dish" className="w-full h-full object-cover hover:scale-105 transition-transform duration-700" />
            </div>
            <div className="overflow-hidden rounded-3xl aspect-[3/4] mt-12">
              <img src={IMG.restaurantHero} alt="The Garden Cafe pavilion" className="w-full h-full object-cover hover:scale-105 transition-transform duration-700" />
            </div>
          </div>
        </div>
      </section>

      {/* Food showcase gallery */}
      <section className="container-px mx-auto mt-16" data-testid="food-gallery">
        <Reveal>
          <div className="flex items-end justify-between gap-4 mb-8">
            <div>
              <div className="eyebrow mb-2">From our kitchen</div>
              <h2 className="font-display text-3xl sm:text-4xl text-ink">Freshly made, beautifully plated</h2>
            </div>
          </div>
        </Reveal>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
          {FOOD_GALLERY.map((src, i) => (
            <Reveal key={src} delay={i * 0.04}>
              <div className={`overflow-hidden rounded-2xl ${i === 0 ? "md:row-span-2 aspect-[3/4]" : "aspect-square"} group`}>
                <img src={src} alt={`Dish ${i + 1}`} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <div className="container-px mx-auto pb-24">
        <Section id="breakfast" title={c.breakfast} subtitle={`Available ${HOURS.breakfast}`}>
          {BREAKFAST.map((m) => <PriceRow key={m.id} name={m.name} price={m.price} desc={m.desc} />)}
          <div className="py-3 text-sm text-ink/60 italic">{t.restaurant.includedNote}</div>
        </Section>

        <Section id="indonesian" title="Main Course — Indonesian Comfort Food" subtitle={`Available ${HOURS.main}`}>
          {INDONESIAN.map((m) => <PriceRow key={m.name} name={m.name} price={m.price} desc={m.desc} />)}
        </Section>

        <Section id="western" title="Western — Classic Comfort Food" subtitle={`Available ${HOURS.main}`}>
          {WESTERN.map((m) => <PriceRow key={m.name} name={m.name} price={m.price} desc={m.desc} />)}
        </Section>

        <div className="grid md:grid-cols-2 gap-6 mt-6">
          <Section id="snacks" title="Snacks" subtitle="Light bites & crispy snacks">
            {SNACKS.map((m) => <PriceRow key={m.name} name={m.name} price={m.price} desc={m.desc} />)}
          </Section>
          <Section id="desserts" title="Desserts" subtitle="Sweet tropical treats">
            {DESSERTS.map((m) => <PriceRow key={m.name} name={m.name} price={m.price} desc={m.desc} />)}
          </Section>
        </div>

        <div className="grid md:grid-cols-2 gap-6 mt-6">
          <Section id="coffee" title="Coffee" subtitle="Hot · Ice">
            {COFFEE.map((m) => <HotIceRow key={m.name} {...m} />)}
          </Section>
          <Section id="non-coffee" title="Non-Coffee" subtitle="Hot · Ice">
            {NON_COFFEE.map((m) => <HotIceRow key={m.name} {...m} />)}
          </Section>
        </div>

        <Section id="juices" title="Juice" subtitle="Fresh pressed daily · No sugar added">
          <div className="py-2">
            <div className="flex items-center gap-2 mb-3 mt-2">
              <Sparkles className="w-4 h-4 text-moss" />
              <div className="eyebrow">Signature Juice · {formatIDR(45000)}</div>
            </div>
            {JUICES_SIGNATURE.map((j) => (
              <div key={j.name} className="flex items-baseline justify-between gap-4 py-3 border-b border-border last:border-0">
                <div>
                  <div className="text-ink">{j.name}</div>
                  <div className="text-xs text-ink/55 italic mt-1">{j.ingredients}</div>
                </div>
                <div className="font-display text-ink whitespace-nowrap">{formatIDR(j.price)}</div>
              </div>
            ))}
          </div>
          <div className="py-2 mt-4">
            <div className="eyebrow mb-3">Fresh Fruit Juice · {formatIDR(35000)}</div>
            {JUICES_FRUIT.map((j) => (
              <PriceRow key={j.name} name={j.name} price={j.price} />
            ))}
          </div>
        </Section>

        <Section id="bowls" title="Smoothie Bowls" subtitle={`Refreshing, healthy & delicious · Available ${HOURS.main}`}>
          {SMOOTHIE_BOWLS.map((m) => <PriceRow key={m.name} name={m.name} price={m.price} desc={m.desc} />)}
        </Section>
      </div>
    </div>
  );
}
